/**
 * @fileoverview DashboardHeader's notification bell and dropdown.
 * @description Split out of DashboardHeader purely to keep that component's own render function
 * short — owns its own open/close state (outside-click/Escape-to-close is useOutsideClick) and
 * renders the bell trigger (with the unread-count badge) plus the dropdown listing each
 * notification. Takes the notification list and mark-read callbacks as props rather than calling
 * the notifications feature itself, since `shared/` may not import a feature (ADR-0002) —
 * DashboardShell (the app/ layer) resolves the list and mutations and passes them down, the same
 * boundary its own doc comment already calls out for privileges/unreadCount/bank.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useOutsideClick } from '@/shared/hooks/useOutsideClick'

// The plain item DashboardShell maps features/notifications' own `InAppNotification` into — not
// imported, since `shared/` may not import a feature (ADR-0002); DashboardShell (the app/ layer)
// is what actually reads the real type and passes plain data down.
export interface DashboardHeaderNotificationItem {
  id: string
  title: string
  body: string
  sentAt: string
  read: boolean
}

interface DashboardHeaderNotificationsProps {
  items: DashboardHeaderNotificationItem[]
  unreadCount: number
  onMarkAllRead: () => void
  onItemClick: (id: string) => void
}

const RELATIVE_TIME_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['week', 604_800],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
]

const relativeTimeFormatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

/**
 * @description Formats an ISO timestamp as a short relative string (e.g. "3 hours ago").
 * @param {string} isoTimestamp - The notification's `sentAt` value.
 * @returns {string} A relative-time string, or "just now" for anything under a minute old.
 */
function formatSentAt(isoTimestamp: string): string {
  const elapsedSeconds = (Date.now() - new Date(isoTimestamp).getTime()) / 1000

  for (const [unit, unitSeconds] of RELATIVE_TIME_UNITS) {
    if (elapsedSeconds >= unitSeconds) {
      return relativeTimeFormatter.format(-Math.floor(elapsedSeconds / unitSeconds), unit)
    }
  }
  return relativeTimeFormatter.format(0, 'minute')
}

interface NotificationRowProps {
  item: DashboardHeaderNotificationItem
  onClick: () => void
}

/**
 * @description One notification row in the dropdown — an unread dot, title, body, and relative
 * timestamp. Clicking an unread row marks it read; a read row is inert (no-op click).
 * @param {NotificationRowProps} props - The notification and its click handler.
 * @param {DashboardHeaderNotificationItem} props.item - The notification to render.
 * @param {() => void} props.onClick - Called on click when the notification is still unread.
 */
function NotificationRow({ item, onClick }: NotificationRowProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={() => !item.read && onClick()}
      className={`flex w-full items-start gap-2.5 border-b border-divider px-3.5 py-2.5 text-left last:border-b-0 hover:bg-input-bg ${
        item.read ? '' : 'bg-info-bg/40'
      }`}
    >
      <span
        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${item.read ? 'bg-transparent' : 'bg-info'}`}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold text-ink">{item.title}</span>
        <span className="block truncate text-xs text-slate">{item.body}</span>
        <span className="mt-0.5 block text-[11px] text-placeholder">
          {formatSentAt(item.sentAt)}
        </span>
      </span>
    </button>
  )
}

/**
 * @component
 * @param {DashboardHeaderNotificationsProps} props - The notification list, unread count, and
 * mark-read callbacks.
 * @param {DashboardHeaderNotificationItem[]} props.items - The signed-in user's notification
 * history, most recent first.
 * @param {number} props.unreadCount - How many unread notifications to badge the bell with.
 * @param {() => void} props.onMarkAllRead - Called when "Mark all as read" is clicked.
 * @param {(id: string) => void} props.onItemClick - Called with a notification's id when an unread
 * row is clicked.
 */
function DashboardHeaderNotifications({
  items,
  unreadCount,
  onMarkAllRead,
  onItemClick,
}: DashboardHeaderNotificationsProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const panelRef = useOutsideClick<HTMLDivElement>(open, close)

  return (
    <div className="relative shrink-0" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('layout.header.notifications.trigger')}
        className="relative flex h-[34px] w-[34px] shrink-0 cursor-pointer items-center justify-center rounded-lg hover:bg-input-bg"
      >
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          className="text-label"
          aria-hidden="true"
        >
          <path d="M18 15.2V10a6 6 0 10-12 0v5.2L4.6 17.4h14.8z" />
          <path d="M10.2 20.4a2.1 2.1 0 003.6 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-[17px] min-w-[17px] items-center justify-center rounded-full border-[1.5px] border-white bg-danger px-1 text-[10.5px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+8px)] z-20 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-divider bg-white shadow-[0_14px_36px_rgba(11,26,52,0.18)]"
        >
          <div className="flex items-center justify-between border-b border-divider px-3.5 py-2.5">
            <p className="text-[13.5px] font-semibold text-ink">
              {t('layout.header.notifications.title')}
            </p>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={onMarkAllRead}
                className="cursor-pointer text-xs font-semibold text-primary hover:underline"
              >
                {t('layout.header.notifications.markAllRead')}
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-3.5 py-6 text-center text-[13px] text-slate">
                {t('layout.header.notifications.empty')}
              </p>
            ) : (
              items.map((item) => (
                <NotificationRow key={item.id} item={item} onClick={() => onItemClick(item.id)} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardHeaderNotifications
